import * as luxon from "luxon";
import { DateTime } from "luxon";
import { RRule, Options, Weekday } from "rrule";

export namespace time_format_utils {
	/**
	 * This should be the primary function we use from now on to display dates and times, for consistency. Note that if year !== current year, it will always show the year, and if minutes === 0, it will truncate the minutes
	 */
	export function displayDateTime(
		datetime: DateTime,
		{
			time,
			date,
			weekday,
		}: {
			time?: boolean;
			date?: boolean;
			weekday?: boolean;
		}
	) {
		const local_datetime = datetime.toLocal();

		const should_show_year =
			date && DateTime.local().year !== local_datetime.year;

		const should_show_minutes = time && local_datetime.minute !== 0;

		return datetime
			.toLocaleString({
				month: date ? "long" : undefined,
				day: date ? "numeric" : undefined,
				hour: time ? "numeric" : undefined,
				year: should_show_year ? "numeric" : undefined,
				minute: should_show_minutes ? "numeric" : undefined,
				weekday: weekday ? "long" : undefined,
			})
			.replaceAll(" PM", "pm")
			.replaceAll(" AM", "am");
	}

	export function date_utc_to_luxon(
		date_utc: Date,
		output_timezone_name: string | undefined
	): luxon.DateTime {
		const luxon_time = luxon.DateTime.fromJSDate(date_utc)
			.toUTC()
			.setZone(output_timezone_name);

		return luxon_time;
	}

	export function formatDurationNew(
		duration_ms: number,
		now_threshold_ms?: number
	) {
		const now = luxon.DateTime.now();
		const future = now.plus({ milliseconds: duration_ms });

		return format_relative_date_concise(future, now_threshold_ms, now);
	}

	export function format_relative_date_concise(
		time_luxon: luxon.DateTime,
		now_threshold_ms?: number,
		now?: luxon.DateTime
	): string {
		const parsed_now = now ?? luxon.DateTime.now();

		let duration_in_ms = time_luxon.diff(parsed_now).milliseconds;

		// If below rounding_value, round down, else round up
		const rounding_value = 0.85;
		const round_offset = rounding_value - 0.5;

		const past = duration_in_ms < 0;
		duration_in_ms = Math.abs(duration_in_ms);
		// 0.9 -> 0.5 -> 0
		// 0.5 -> 0.1 -> 0
		// 1.4 -> 0.9

		if (now_threshold_ms != null && duration_in_ms < now_threshold_ms) {
			return "now";
		}

		const years = Math.round(
			duration_in_ms / (1000 * 60 * 60 * 24 * 365) - round_offset
		);

		if (years >= 1) {
			return format_duration(years, "year", past);
		}
		const months = Math.round(
			duration_in_ms / (1000 * 60 * 60 * 24 * 30) - round_offset
		);
		if (months >= 1) {
			return format_duration(months, "month", past);
		}

		const days = Math.round(
			duration_in_ms / (1000 * 60 * 60 * 24) - round_offset
		);

		if (days >= 1) {
			return format_duration(days, "day", past);
		}

		const hours = Math.round(
			duration_in_ms / (1000 * 60 * 60) - round_offset
		);

		if (hours >= 1) {
			return format_duration(hours, "hour", past);
		}

		const minutes = Math.round(duration_in_ms / (1000 * 60) - round_offset);

		if (minutes >= 1) {
			return format_duration(minutes, "minute", past);
		}

		const seconds = Math.round(duration_in_ms / 1000 - round_offset);

		if (seconds >= 1) {
			return format_duration(seconds, "second", past);
		}

		return "now";
	}

	export function format_duration(value: number, unit: string, past = false) {
		const single = value === 1;

		let first_word = "";
		let end_word = "";
		if (!past) {
			first_word = "in ";
			end_word = "";
		} else {
			first_word = "";
			end_word = " ago";
		}

		let amount_text = "";
		let pluralized_str = "";
		if (single) {
			amount_text = "a";
			if (unit === "hour") {
				amount_text = "an";
			}
		} else {
			amount_text = `${value}`;
			pluralized_str = "s";
		}

		return `${first_word}${amount_text} ${unit}${pluralized_str}${end_word}`;
	}

	export function formatRRule(
		rrule_str: string,
		output_timezone: string,
		start_time_luxon: luxon.DateTime,
		use_discord_timestamps = false
	) {
		const modified_rrule_options =
			time_format_utils.transformRRuleToTimezone(
				RRule.parseString(rrule_str),
				output_timezone,
				start_time_luxon.toUTC(),
				true
			);

		const rrule = new RRule(modified_rrule_options).toText();

		// Capitalize first letter
		let rrule_text = rrule.charAt(0).toUpperCase() + rrule.substring(1);

		if (use_discord_timestamps) {
			const has_timezone_dependent_properties =
				"byweekday" in modified_rrule_options ||
				"bymonthday" in modified_rrule_options ||
				"bysetpos" in modified_rrule_options;

			if (has_timezone_dependent_properties) {
				const timezone_offset =
					luxon.DateTime.now().setZone(
						output_timezone
					).offsetNameShort;

				rrule_text += ` ${timezone_offset}`;
			}
		}

		return rrule_text;
	}

	export function getDSTOffset(
		zone: string,
		utc_date_1: luxon.DateTime,
		utc_date_2: luxon.DateTime
	) {
		const local_date_1 = utc_date_1.setZone(zone);

		if (local_date_1.isOffsetFixed) {
			return 0;
		}

		const local_date_2 = utc_date_2.setZone(zone);
		if (local_date_1.offset == local_date_2.offset) {
			return 0;
		}

		return local_date_1.offset - local_date_2.offset;
	}

	export function format_time_concise_for_web_ui(
		start_time_luxon: luxon.DateTime,
		end_time_luxon: luxon.DateTime,
		timezone: string,
		use_24hr_clock: boolean,
		link?: string
	) {
		let time_string = "";
		if (end_time_luxon) {
			const start_time_clock_string = `${get_clock_time(
				start_time_luxon,
				null,
				use_24hr_clock
			)}`;

			let end_time_clock_string = `${get_clock_time(
				end_time_luxon,
				timezone,
				use_24hr_clock
			)}`;

			const is_same_day =
				start_time_luxon.day === end_time_luxon.day &&
				start_time_luxon.month === end_time_luxon.month &&
				start_time_luxon.year === end_time_luxon.year;

			if (!is_same_day) {
				const verbose_end_time_string = end_time_luxon.toLocaleString({
					month: "short",
					day: "2-digit",
				});

				end_time_clock_string += ` ${verbose_end_time_string}`;
			}

			time_string = `${start_time_clock_string} – ${end_time_clock_string}`;
		} else {
			time_string = `${get_clock_time(
				start_time_luxon,
				timezone,
				use_24hr_clock
			)}`;
		}

		if (link) {
			time_string = `[${time_string}](${link})`;
		}

		return `${start_time_luxon.toLocaleString({
			year: "2-digit",
			month: "2-digit",
			day: "2-digit",
		})} ⋅ ${time_string}`;
	}

	export function format_time_concise_date_first(time_luxon: luxon.DateTime) {
		let result = time_luxon.toLocaleString({
			weekday: "short",
			month: "short",
			day: "2-digit",
		});

		result += ` at ${get_clock_time(time_luxon)}`;
		return result;
	}

	export function formatTimeConciseV2(
		time_luxon: luxon.DateTime,
		include_relative = false
	) {
		let result = getClockTimeV2(time_luxon);

		result += ` on ${time_luxon.toLocaleString({
			weekday: "short",
			month: "short",
			day: "2-digit",
		})}`;

		if (include_relative) {
			result += ` (${time_format_utils.format_relative_date_concise(
				time_luxon
			)})`;
		}

		return result;
	}

	export function format_time_concise(
		time_luxon: luxon.DateTime,
		timezone?: string,
		use_24h_clock?: boolean,
		include_relative?: boolean
	) {
		let result = `${get_clock_time(time_luxon, timezone, use_24h_clock)}`;

		result += ` on ${time_luxon.toLocaleString({
			weekday: "short",
			month: "short",
			day: "2-digit",
		})}`;

		if (include_relative) {
			result += ` (${time_format_utils.format_relative_date_concise(
				time_luxon
			)})`;
		}

		return result;
	}

	export function shiftRRuleIfNeededDueToDST(
		rrule: string,
		prev_start_time: DateTime,
		current_start_time: DateTime
	) {
		// The hour changed by more than 1 since last repeat. This is a hacky way to indicate that the time moved to a separate day due to DST
		// i.e. Wednesday at 12:30am UTC becomes Tuesday at 11:30pm UTC, (an hour difference of 23) so we need to modify the RRule to account for this change
		// An example is an RRule that repeats every Wednesday. Because we regrettably store RRules as UTC repeat times, we need to convert it to every Tuesday in the repeat timezone
		if (Math.abs(prev_start_time.hour - current_start_time.hour) <= 6) {
			return RRule.parseString(rrule);
		}

		// ex: prev_start_time.hour = 23, current_start_time.hour = 0
		// shift RRule forward by 1 day
		const should_shift_forward =
			prev_start_time.hour > current_start_time.hour;

		return modifyRRuleDueToShift(
			RRule.parseString(rrule),
			RRule.parseString(rrule),
			should_shift_forward
		);
	}

	function modifyRRuleDueToShift(
		orig_options: Partial<Options>,
		result_options: Partial<Options>,
		shift_forward: boolean
	) {
		const weekdays = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

		if (result_options.byweekday) {
			// TODO: ensure its always an array
			if (Array.isArray(orig_options.byweekday)) {
				let i = 0;
				for (const weekday_data of result_options.byweekday as Weekday[]) {
					let weekday = weekday_data.weekday;
					if (shift_forward) {
						weekday += 1;
						if (weekday > weekdays.length - 1) {
							weekday = 0;
						}
					} else {
						weekday -= 1;
						if (weekday < 0) {
							weekday = weekdays.length - 1;
						}
					}
					result_options.byweekday[i] = new Weekday(weekday);

					i++;
				}
			}
		}

		if (result_options.bymonthday) {
			if (typeof result_options.bymonthday === "number") {
				let monthday = result_options.bymonthday;
				if (shift_forward) {
					monthday++;
					if (monthday > 31) {
						monthday = 1;
						if (
							result_options.bymonth &&
							typeof result_options.bymonth === "number"
						) {
							result_options.bymonth++;
							if (result_options.bymonth > 12) {
								result_options.bymonth = 1;
							}
						}
					}
				} else {
					monthday--;
					if (monthday < 1) {
						monthday = 31;
						if (
							result_options.bymonth &&
							typeof result_options.bymonth === "number"
						) {
							result_options.bymonth--;
							if (result_options.bymonth < 1) {
								result_options.bymonth = 12;
							}
						}
					}
				}
				result_options.bymonthday = monthday;
			}
		}

		if (result_options.until) {
			// TODO: handle 'until'
		}

		return result_options;
	}

	// Convert base rrule into timezoned rrule
	export function transformRRuleToTimezone(
		rrule_orig_options: Partial<Options>,
		to_zone: string,
		repeat_time_utc: luxon.DateTime,
		to_local = false
	) {
		const repeat_time_with_zone = repeat_time_utc.setZone(to_zone);

		const result_options = RRule.parseString(
			RRule.optionsToString(rrule_orig_options)
		);

		if (repeat_time_with_zone.day === repeat_time_utc.day) {
			return result_options;
		}

		let shift_forward: boolean | undefined = undefined;
		if (!to_local) {
			shift_forward =
				new Date(
					repeat_time_with_zone.year,
					repeat_time_with_zone.month - 1,
					repeat_time_with_zone.day
				) <
				new Date(
					repeat_time_utc.year,
					repeat_time_utc.month - 1,
					repeat_time_utc.day
				);
		} else {
			shift_forward =
				new Date(
					repeat_time_with_zone.year,
					repeat_time_with_zone.month - 1,
					repeat_time_with_zone.day
				) >
				new Date(
					repeat_time_utc.year,
					repeat_time_utc.month - 1,
					repeat_time_utc.day
				);
		}

		return modifyRRuleDueToShift(
			rrule_orig_options,
			result_options,
			shift_forward
		);
	}

	export function format_time_super_concise(
		time_luxon: luxon.DateTime,
		timezone?: string,
		use_24_clock?: boolean
	) {
		let result = `${time_luxon.toLocaleString({
			weekday: "short",
			month: "short",
			day: "2-digit",
		})}`;

		result = result.replace(",", "");

		result += ` at ${get_clock_time(time_luxon, null, use_24_clock)}`;

		return result;
	}

	export function format_time_verbose_without_until(
		time_luxon: luxon.DateTime,
		timezone?: string,
		use_24h_clock?: boolean
	) {
		let result = `${get_clock_time(time_luxon, timezone, use_24h_clock)}`;

		result += ` on ${time_luxon.toLocaleString({
			weekday: "long",
			month: "short",
			day: "2-digit",
		})}`;

		return result;
	}

	export function format_time_verbose_date_first(time_luxon: DateTime) {
		return `${time_luxon.toLocaleString({
			weekday: "long",
			month: "long",
			day: "2-digit",
			year: "numeric",
		})} ${get_clock_time(time_luxon)}`;
	}

	export function format_time_verbose(
		time_luxon: luxon.DateTime,
		timezone?: string,
		link?: string,
		use_24h_clock?: boolean,
		duration_ms?: number
	) {
		let result =
			`${get_clock_time(time_luxon, timezone, use_24h_clock)}` +
			(duration_ms
				? ` - ${get_clock_time(
						time_luxon.plus({ milliseconds: duration_ms }),
						timezone,
						use_24h_clock
				  )}`
				: "");

		if (link) {
			result = `[${result}](${link})`;
		}

		result += ` on ${time_luxon.toLocaleString({
			weekday: "long",
			month: "short",
			day: "2-digit",
		})} (${format_relative_date_concise(time_luxon)})`;

		return result;
	}

	export function format_time_from_date_picker(time_luxon: luxon.DateTime) {
		// explicitly set the locale because if we do not, itll parse as 24 hour time which chrono does not like
		time_luxon = time_luxon.setLocale("en-US");
		const time = time_luxon.toLocaleString({
			timeStyle: "short",
		});

		const result = `${time} on ${time_luxon.toLocaleString(
			{
				weekday: "long",
				month: "short",
				day: "2-digit",
				year: "numeric",
			},
			{
				locale: "en-US",
			}
		)}`;

		return result;
	}

	export function format_time_verbose_dual_timezone(
		local_time_luxon: luxon.DateTime,
		local_timezone,
		guild_timezone,
		use_24h_clock?: boolean,
		duration_ms?: number
	) {
		const local_time_string =
			`${get_clock_time(local_time_luxon, null, use_24h_clock)}` +
			(duration_ms
				? ` - ${get_clock_time(
						local_time_luxon.plus({ milliseconds: duration_ms }),
						null,
						use_24h_clock
				  )}`
				: "");
		const guild_time_luxon = local_time_luxon.setZone(guild_timezone);
		const guild_time_string =
			`${get_clock_time(guild_time_luxon, null, use_24h_clock)}` +
			(duration_ms
				? ` - ${get_clock_time(
						guild_time_luxon.plus({ milliseconds: duration_ms }),
						null,
						use_24h_clock
				  )}`
				: "");

		const local_timezone_abbr = time_format_utils.get_timezone_abbr(
			local_time_luxon,
			local_timezone
		);
		const guild_timezone_abbr = time_format_utils.get_timezone_abbr(
			local_time_luxon,
			guild_timezone
		);

		let result = `${local_time_string} ${local_timezone_abbr} / ${guild_time_string} ${guild_timezone_abbr}`;

		result += ` on ${local_time_luxon.toLocaleString({
			weekday: "long",
			month: "short",
			day: "2-digit",
		})} (${format_relative_date_concise(local_time_luxon)})`;

		return result;
	}

	export function format_time_verbose_no_relative_time(
		start_time_luxon: luxon.DateTime,
		timezone?: string,
		link?: string,
		end_time_luxon?: luxon.DateTime,
		use_24hr_clock?: boolean,
		use_discord_timestamps = false
	) {
		let time_string: string | null = null;

		if (end_time_luxon) {
			const start_time_clock_string = `${get_clock_time(
				start_time_luxon,
				null,
				use_24hr_clock
			)}`;

			let end_time_clock_string = `${get_clock_time(
				end_time_luxon,
				timezone,
				use_24hr_clock
			)}`;

			const is_same_day =
				start_time_luxon.day === end_time_luxon.day &&
				start_time_luxon.month === end_time_luxon.month &&
				start_time_luxon.year === end_time_luxon.year;

			if (!is_same_day) {
				const verbose_end_time_string = end_time_luxon.toLocaleString({
					month: "short",
					day: "2-digit",
				});

				end_time_clock_string += ` ${verbose_end_time_string}`;
			}

			time_string = `${start_time_clock_string} – ${end_time_clock_string}`;
		} else {
			time_string = `${get_clock_time(
				start_time_luxon,
				timezone,
				use_24hr_clock,
				true
			)}`;
		}

		if (link) {
			time_string = `[[${time_string}]](${link})`;
		}

		let separator: string;
		let month_format: "short" | "numeric" | "2-digit" | "long" | "narrow";
		if (use_discord_timestamps) {
			separator = " ";
			month_format = "long";
		} else {
			separator = " ⋅ ";
			month_format = "short";
		}

		const now = time_format_utils.date_utc_to_luxon(new Date(), timezone);

		const year_differs = start_time_luxon.year !== now.year;

		return `${start_time_luxon.toLocaleString({
			year: year_differs ? "numeric" : undefined,
			weekday: "long",
			month: month_format,
			day: "2-digit",
		})}${separator}${time_string}`;
	}

	export function refine_to_24_hour_clock(chrono_result: any) {
		const start = chrono_result?.start;
		const end = chrono_result?.end;

		if (start?.impliedValues?.meridiem) {
			let hour;
			if (start?.knownValues?.hour) {
				hour = start.knownValues.hour;
			} else if (start?.impliedValues?.hour) {
				hour = start.impliedValues.hour;
			}
			if (hour >= 12) {
				start.impliedValues.hour = hour - 12;
				delete start.knownValues.hour;

				if (end) {
					let end_hour;
					if (end?.knownValues?.hour) {
						end_hour = end.knownValues.hour;
					} else if (end?.impliedValues?.hour) {
						end_hour = end.impliedValues.hour;
					}

					let hour_diff = 0;
					if (end_hour > hour) {
						hour_diff = end_hour - hour;
					} else {
						hour_diff = 24 - hour + hour;
					}

					end.impliedValues.hour =
						(start.impliedValues.hour + hour_diff) % 24;
					delete end.knownValues.hour;
				}
			}
		}

		if (end?.impliedValues?.meridiem) {
			let hour;
			if (end?.knownValues?.hour) {
				hour = end.knownValues.hour;
			} else if (end?.impliedValues?.hour) {
				hour = end.impliedValues.hour;
			}
			if (hour >= 12) {
				end.impliedValues.hour = hour - 12;
				delete end.knownValues.hour;
			}
		}
	}

	export function getClockTimeV2(time: DateTime) {
		return time
			.toLocaleString({
				timeStyle: "short",
			})
			.toLowerCase()
			.replaceAll(" ", "");
	}

	export function get_clock_time(
		time_luxon: luxon.DateTime,
		timezone?: string | null,
		use_24h_clock?: boolean,
		do_not_truncate = false
	) {
		const time_rounded_to_hour_luxon = time_luxon.startOf("hour");

		let result = "";

		if (!use_24h_clock) {
			if (do_not_truncate) {
				result = `${time_luxon.toFormat("hh:mma").toLowerCase()}`;
			} else {
				if (time_luxon.equals(time_rounded_to_hour_luxon)) {
					result = `${time_luxon.toFormat("ha").toLowerCase()}`;
				} else {
					result = `${time_luxon.toFormat("h:mma").toLowerCase()}`;
				}
			}
		} else {
			if (do_not_truncate) {
				result = `${time_luxon.toFormat("T")}`;
			} else {
				result = `${time_luxon.toFormat("HH:mm")}`;
			}
		}

		if (timezone) {
			result += " " + get_timezone_abbr(time_luxon, timezone);
		}

		return result;
	}

	export function get_timezone_abbr_verbose(
		time_luxon: luxon.DateTime,
		timezone: string
	): string {
		return time_luxon.setZone(timezone).toFormat("ZZZZ ZZ");
	}

	export function get_timezone_abbr(
		time_luxon: luxon.DateTime,
		timezone: string
	) {
		return time_luxon.setZone(timezone).offsetNameShort;
	}

	export function get_embed_timezone_description(
		date: Date,
		timezone: string
	): string {
		const current_date_time = date_utc_to_luxon(date, timezone);
		return `🌐 ${get_timezone_abbr_verbose(current_date_time, timezone)}`;
	}

	export function get_month_day_concise(
		time_luxon: luxon.DateTime,
		include_year_if_differs = false,
		output_timezone_name?: string
	) {
		let format_string = "LLL dd";
		if (include_year_if_differs) {
			const now = time_format_utils.date_utc_to_luxon(
				new Date(),
				output_timezone_name
			);

			const year_differs = time_luxon.year !== now.year;
			if (year_differs) {
				format_string += " yyyy";
			}
		}

		return `${time_luxon.toFormat(format_string)}`;
	}

	export function get_month_day_super_concise(time_luxon: luxon.DateTime) {
		return `${time_luxon.toFormat("L/d")}`;
	}

	export function formatDurationVerbose(duration: luxon.Duration) {
		duration = duration.shiftTo("days", "hours", "minutes", "seconds");

		let duration_formatted = "";
		if (duration.days >= 1) {
			duration_formatted += Math.floor(duration.days) + " day";
			if (Math.floor(duration.days) > 1) {
				duration_formatted += "s";
			}
			duration_formatted += " ";
		}
		if (duration.hours >= 1) {
			duration_formatted += Math.floor(duration.hours) + " hour";
			if (Math.floor(duration.hours) > 1) {
				duration_formatted += "s";
			}
			duration_formatted += " ";
		}
		if (duration.minutes >= 1) {
			duration_formatted += Math.floor(duration.minutes) + " minute";
			if (Math.floor(duration.minutes) > 1) {
				duration_formatted += "s";
			}
			duration_formatted += " ";
		}
		if (duration.seconds >= 0) {
			if (!duration_formatted || duration.seconds > 0) {
				duration_formatted += Math.floor(duration.seconds) + " second";
				if (Math.floor(duration.seconds) !== 1) {
					duration_formatted += "s";
				}
				duration_formatted += " ";
			}
		}

		return duration_formatted.trim();
	}

	export function formatDuration(start_time: Date, end_time: Date) {
		const luxon_duration = luxon.DateTime.fromJSDate(end_time).diff(
			luxon.DateTime.fromJSDate(start_time),
			["days", "hours", "minutes", "seconds"]
		);

		let duration_formatted = "";
		if (luxon_duration.days >= 1) {
			duration_formatted += Math.floor(luxon_duration.days) + "d ";
		}
		if (luxon_duration.hours >= 1) {
			duration_formatted += Math.floor(luxon_duration.hours) + "h ";
		}
		if (luxon_duration.minutes >= 1) {
			duration_formatted += Math.floor(luxon_duration.minutes) + "m ";
		}
		if (luxon_duration.seconds >= 1) {
			duration_formatted += Math.floor(luxon_duration.seconds) + "s ";
		}

		if (duration_formatted.length === 0) {
			duration_formatted += "0s ";
		}

		return duration_formatted;
	}

	export function areTimezonesEquivalent(
		a: string,
		b: string,
		ref_time: luxon.DateTime | null
	) {
		if (a === b) {
			return true;
		}

		// double check timezone utc offsets actually differ
		const personal_timezone_luxon = luxon.IANAZone.create(a);
		const guild_timezone_luxon = luxon.IANAZone.create(b);

		const time_for_offset_calculation = ref_time
			? ref_time.toMillis()
			: luxon.DateTime.local().toMillis();

		const personal_offset = personal_timezone_luxon.offset(
			time_for_offset_calculation
		);
		const guild_offset = guild_timezone_luxon.offset(
			time_for_offset_calculation
		);

		return personal_offset === guild_offset;
	}
}
